import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { GradientButton } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { SCHOOL_START_DEPLOYMENT } from '../../lib/mockSchool';
import { submitForm } from '../../lib/api';
import { Minus, Plus, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';

const inputCls = 'w-full rounded-lg border border-canvas-200 bg-white px-4 py-2.5 text-[14px] text-navy placeholder:text-ink-500/60 focus:outline-none focus:ring-2 focus:ring-purple/40';
const labelCls = 'text-[13px] font-semibold text-ink-700';

function Stepper({ steps, current }) {
  return (
    <ol className="grid grid-cols-3 gap-2" data-testid="deployment-stepper">
      {steps.map((s) => {
        const state = current === s.n ? 'active' : current > s.n ? 'done' : 'pending';
        return (
          <li key={s.n} data-testid={`deployment-step-${s.n}-${state}`} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-[13px] font-semibold ${state === 'active' ? 'border-purple bg-purple-50 text-purple' : state === 'done' ? 'border-mint bg-mint-50 text-mint-600' : 'border-canvas-200 bg-white text-ink-500'}`}>
            <span className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold ${state === 'active' ? 'bg-purple text-white' : state === 'done' ? 'bg-mint text-white' : 'bg-canvas-200 text-ink-700'}`}>{state === 'done' ? '✓' : s.n}</span>
            <span>{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function NumberStepper({ value, onChange, testid }) {
  const dec = () => onChange(Math.max(0, (value || 0) - 1));
  const inc = () => onChange((value || 0) + 1);
  return (
    <div className="mt-2 flex items-center gap-2">
      <button type="button" onClick={dec} aria-label="decrement" data-testid={`${testid}-dec`} className="grid h-10 w-10 place-items-center rounded-lg border border-canvas-200 bg-white text-navy hover:bg-canvas-100"><Minus className="h-4 w-4" /></button>
      <input type="number" min="0" value={value} onChange={e => onChange(Math.max(0, parseInt(e.target.value || '0', 10)))} data-testid={testid} className={`w-24 text-center ${inputCls}`} />
      <button type="button" onClick={inc} aria-label="increment" data-testid={`${testid}-inc`} className="grid h-10 w-10 place-items-center rounded-lg border border-canvas-200 bg-white text-navy hover:bg-canvas-100"><Plus className="h-4 w-4" /></button>
    </div>
  );
}

export default function SchoolStartDeployment() {
  const d = SCHOOL_START_DEPLOYMENT;
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [form, setForm] = useState({
    // Step 1
    school: '', type: '', city: '', district: '', website: '',
    contact: '', role: '', email: '', phone: '',
    // Step 2
    g8: 0, g9: 0, setup: '', intended: 'Curriculum modules', accounts: '',
    // Step 3
    checks: [false, false, false, false, false],
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleCheck = (i) => setForm(p => ({ ...p, checks: p.checks.map((c, ci) => ci === i ? !c : c) }));

  const allChecked = form.checks.every(Boolean);

  const next = () => { if (step < 3) setStep(step + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const prev = () => { if (step > 1) setStep(step - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allChecked || submitting) return;
    setSubmitting(true); setSubmitError(null);
    try {
      await submitForm({
        formType: 'school-start-deployment',
        fields: form,
        contactEmail: form.email,
        contactName: form.contact,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PageShell>
        <section className="px-6 py-24" data-testid="deployment-confirmation">
          <div className="mx-auto max-w-[720px]">
            <div className="rounded-3xl border border-mint/30 bg-gradient-to-br from-mint-50 via-cyan-50 to-purple-50 p-10 shadow-soft text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-mint text-white shadow"><CheckCircle2 className="h-8 w-8" /></div>
              <h2 className="mt-5 text-[26px] md:text-[30px] font-extrabold text-navy">{d.confirmation.title}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-700 max-w-2xl mx-auto">{d.confirmation.body}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <GradientButton to="/school">Back to Overview</GradientButton>
                <GradientButton to="/school/faq" variant="outline">Read FAQs</GradientButton>
              </div>
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="school-start-deployment-hero">
        <div className="mx-auto max-w-[1240px]">
          <h1 className="text-[40px] md:text-[52px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
          <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
          <div className="mt-6"><LicenceFeeHighlight /></div>
        </div>
      </section>

      {/* FORM */}
      <section className="px-6 py-8 pb-24">
        <div className="mx-auto max-w-[960px]">
          <Stepper steps={d.steps} current={step} />

          <form onSubmit={onSubmit} className="mt-6 rounded-3xl border border-canvas-200 bg-white p-6 md:p-10 shadow-soft space-y-6" data-testid="deployment-form">
            <h2 className="text-[20px] font-extrabold text-navy">{d.stepTitles[step]}</h2>

            {step === 1 && (
              <>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="s-school">School or institution name</label>
                    <input id="s-school" required data-testid="dep-school" value={form.school} onChange={e => set('school', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter institution name" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="s-type">School type</label>
                    <select id="s-type" required data-testid="dep-type" value={form.type} onChange={e => set('type', e.target.value)} className={`mt-2 ${inputCls}`}>
                      <option value="">Select school type</option>
                      {d.schoolTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="s-city">City</label>
                    <input id="s-city" required data-testid="dep-city" value={form.city} onChange={e => set('city', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter city" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="s-district">District</label>
                    <input id="s-district" required data-testid="dep-district" value={form.district} onChange={e => set('district', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter district" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="s-website">Website</label>
                    <input id="s-website" type="url" data-testid="dep-website" value={form.website} onChange={e => set('website', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="https://..." />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="s-contact">Contact person</label>
                    <input id="s-contact" required data-testid="dep-contact" value={form.contact} onChange={e => set('contact', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Full name" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="s-role">Role or designation</label>
                    <select id="s-role" required data-testid="dep-role" value={form.role} onChange={e => set('role', e.target.value)} className={`mt-2 ${inputCls}`}>
                      <option value="">Select role</option>
                      {d.roles.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="s-email">Email</label>
                    <input id="s-email" type="email" required data-testid="dep-email" value={form.email} onChange={e => set('email', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="name@school.org" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="s-phone">Phone</label>
                    <input id="s-phone" required data-testid="dep-phone" value={form.phone} onChange={e => set('phone', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="+91" />
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className={labelCls}>Number of Grade 8 students</label>
                    <NumberStepper value={form.g8} onChange={v => set('g8', v)} testid="dep-g8" />
                  </div>
                  <div>
                    <label className={labelCls}>Number of Grade 9 students</label>
                    <NumberStepper value={form.g9} onChange={v => set('g9', v)} testid="dep-g9" />
                  </div>
                </div>
                <div>
                  <div className={labelCls}>Preferred initial setup</div>
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {d.setupPresets.map(p => (
                      <label key={p} className={`cursor-pointer rounded-xl border px-4 py-3 text-center text-[14px] font-semibold shadow-soft transition-colors ${form.setup === p ? 'border-purple bg-purple-50 text-purple' : 'border-canvas-200 bg-white text-navy hover:border-purple/40'}`} data-testid={`dep-setup-${p.replace(/\s+/g, '-').toLowerCase()}`}>
                        <input type="radio" name="setup" value={p} checked={form.setup === p} onChange={e => set('setup', e.target.value)} className="sr-only" />
                        {p}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className={labelCls}>Intended use</div>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {d.intendedUse.map(u => (
                      <label key={u} className={`cursor-pointer rounded-xl border px-4 py-3 text-center text-[14px] font-semibold shadow-soft transition-colors ${form.intended === u ? 'border-purple bg-purple-50 text-purple' : 'border-canvas-200 bg-white text-navy hover:border-purple/40'}`} data-testid={`dep-use-${u.split(' ')[0].toLowerCase()}`}>
                        <input type="radio" name="intended" value={u} checked={form.intended === u} onChange={e => set('intended', e.target.value)} className="sr-only" />
                        {u}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className={labelCls}>Individual student accounts</div>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {d.accountOptions.map(o => (
                      <label key={o.value} className={`cursor-pointer rounded-xl border p-4 shadow-soft transition-colors ${form.accounts === o.value ? 'border-purple bg-purple-50' : 'border-canvas-200 bg-white hover:border-purple/40'}`} data-testid={`dep-accounts-${o.value}`}>
                        <input type="radio" name="accounts" value={o.value} checked={form.accounts === o.value} onChange={e => set('accounts', e.target.value)} className="sr-only" />
                        <div className="text-[14px] font-semibold text-navy">{o.label}</div>
                        {o.note && <div className="mt-1 text-[11.5px] text-ink-500">{o.note}</div>}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <p className="text-[13.5px] text-ink-500">Please confirm each of the following before submitting.</p>
                <div className="space-y-2.5">
                  {d.readinessChecks.map((c, i) => (
                    <label key={i} className={`flex items-start gap-3 rounded-xl border p-4 shadow-soft cursor-pointer transition-colors ${form.checks[i] ? 'border-mint bg-mint-50' : 'border-canvas-200 bg-white hover:border-purple/40'}`} data-testid={`dep-check-${i}`}>
                      <input type="checkbox" checked={form.checks[i]} onChange={() => toggleCheck(i)} className="mt-1 h-4 w-4 rounded border-canvas-200 text-purple focus:ring-purple/40" />
                      <span className="text-[13.5px] text-ink-700">{c}</span>
                    </label>
                  ))}
                </div>
              </>
            )}

            {/* Navigation */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-canvas-200">
              <div>
                {step > 1 && (
                  <button type="button" onClick={prev} data-testid="dep-prev" className="inline-flex items-center gap-1 rounded-lg border border-canvas-200 bg-white px-4 py-2.5 text-[13.5px] font-semibold text-navy hover:bg-canvas-100"><ArrowLeft className="h-4 w-4" /> Back</button>
                )}
              </div>
              <div>
                {step < 3 && (
                  <button type="button" onClick={next} data-testid="dep-next" className="inline-flex items-center gap-1 rounded-lg bg-brand-gradient px-5 py-2.5 text-[13.5px] font-semibold text-white shadow-soft hover:brightness-110">Continue <ArrowRight className="h-4 w-4" /></button>
                )}
                {step === 3 && (
                  <button type="submit" disabled={!allChecked || submitting} data-testid="dep-submit" className={`inline-flex items-center gap-1 rounded-lg px-6 py-3 text-[14px] font-semibold text-white shadow-soft ${allChecked && !submitting ? 'bg-brand-gradient hover:brightness-110' : 'bg-canvas-200 text-ink-500 cursor-not-allowed'}`}>{submitting ? 'Submitting…' : d.submit}</button>
                )}
              </div>
            </div>
            {submitError && (
              <div className="mt-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[12.5px] text-red-700" data-testid="dep-submit-error">{submitError}</div>
            )}
          </form>
        </div>
      </section>
    </PageShell>
  );
}
